'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a complexity weight (higher = more complex/risky to migrate)
// and an optional note that surfaces a planning consideration when chosen.
interface Option {
  text: string;
  weight: number; // 0 (simple) .. 3 (complex)
  note?: string; // planning consideration triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'source_platform',
    text: 'What are you migrating from?',
    helpText: 'The source system drives most of the migration approach and risk.',
    options: [
      { text: 'Google Workspace (Gmail, Drive, shared drives)', weight: 2, note: 'Google-to-M365 needs Gmail, Drive, and shared-drive mapping into Exchange Online, OneDrive, and SharePoint — handled cleanly with BitTitan MigrationWiz.' },
      { text: 'On-prem or hybrid Exchange', weight: 3, note: 'On-prem Exchange adds public folders, archives, and server decommissioning — plan a coexistence period so mail never drops during cutover.' },
      { text: 'Another Microsoft 365 tenant (merger/acquisition)', weight: 3, note: 'Tenant-to-tenant means matching identities, mail, files, Teams, and permissions across two M365 tenants — the most permission-sensitive move.' },
      { text: 'IMAP / POP / hosted email (cPanel, Rackspace, etc.)', weight: 1, note: 'Hosted/IMAP mailboxes migrate relatively cleanly, but calendars, contacts, and rules need explicit handling so nothing is lost.' },
    ],
  },
  {
    id: 'mailbox_count',
    text: 'How many mailboxes / users are you moving?',
    helpText: 'Scale affects wave planning and how long the staged sync runs.',
    options: [
      { text: '1–25', weight: 0 },
      { text: '26–100', weight: 1, note: 'At this size a wave plan by team keeps the cutover orderly and your help desk from being overwhelmed.' },
      { text: '101–500', weight: 2, note: 'Hundreds of mailboxes need a phased, multi-wave migration with coexistence so the business keeps running throughout.' },
      { text: '500+', weight: 3, note: 'Large migrations require careful throttling, multiple sync waves, and a detailed cutover runbook to stay zero-downtime.' },
    ],
  },
  {
    id: 'data_volume',
    text: 'Roughly how much mail and file data is involved?',
    helpText: 'Total data volume sets how long background sync takes before cutover.',
    options: [
      { text: 'Light — mostly email, little shared file storage', weight: 0 },
      { text: 'Moderate — email plus a few hundred GB of files', weight: 1, note: 'Plan to start the background file sync early so the final delta at cutover is small and fast.' },
      { text: 'Heavy — terabytes of SharePoint/Drive/file data', weight: 3, note: 'Terabyte-scale data needs AvePoint for SharePoint/OneDrive/Teams structure and permission-aware moves, started well ahead of cutover.' },
      { text: 'Not sure', weight: 2, note: 'Unknown data volume is itself a risk — a discovery inventory is the first step so the timeline is based on real numbers.' },
    ],
  },
  {
    id: 'permissions',
    text: 'How are shared folders and permissions currently managed?',
    helpText: 'Sharing and permission structure is the part DIY migrations most often break.',
    options: [
      { text: 'Tightly managed — clear ownership and access controls', weight: 0 },
      { text: 'Mostly fine, some legacy shared folders', weight: 1, note: 'Legacy shared folders need their permissions mapped and re-applied so access carries over correctly post-cutover.' },
      { text: 'Lots of broadly shared / "shared with everyone" folders', weight: 3, note: 'Broad oversharing must be mapped before migration and is the exact problem Copilot would later expose — a permission cleanup is recommended.' },
      { text: 'No idea who can access what', weight: 3, note: 'No permission visibility is high-risk for both migration and Copilot. An access audit should come before the move.' },
    ],
  },
  {
    id: 'copilot_interest',
    text: 'Are you planning to roll out Microsoft 365 Copilot?',
    helpText: 'Copilot surfaces whatever a user can access, so readiness depends on clean permissions.',
    options: [
      { text: 'Yes — soon, it is a priority', weight: 3, note: 'Copilot is a priority: a Copilot readiness assessment (access cleanup, sensitivity labels, governance) should run before rollout so oversharing does not become data exposure.' },
      { text: 'Maybe — within the next year', weight: 2, note: 'With Copilot on the horizon, migrating with clean permissions and data governance now avoids expensive rework later.' },
      { text: 'Not planning to right now', weight: 0 },
      { text: 'Not sure what Copilot readiness means', weight: 1, note: 'Copilot answers using whatever a user can access — readiness means cleaning up permissions and labeling sensitive data first. Worth understanding before you enable it.' },
    ],
  },
  {
    id: 'downtime',
    text: 'How much downtime can the business tolerate during cutover?',
    helpText: 'Lower tolerance means a more carefully staged, coexistence-based cutover.',
    options: [
      { text: 'None — mail and files must stay available throughout', weight: 2, note: 'Zero downtime requires a coexistence period and a near-instant delta cutover — exactly the managed approach we run, not a DIY weekend.' },
      { text: 'A weekend or off-hours window is fine', weight: 1, note: 'An off-hours window gives flexibility, but staged background sync still keeps the actual cutover short and low-risk.' },
      { text: 'We can absorb a short outage if needed', weight: 0 },
      { text: 'Not sure', weight: 1, note: 'Define a downtime tolerance early — it shapes the entire cutover plan and whether coexistence is required.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Moderate' | 'Low';
  label: string;
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 60) {
    return {
      level: 'High',
      label: 'High complexity',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'This is a high-complexity migration — the kind where a DIY attempt risks lost email, broken permissions, and downtime. A planned, managed cutover with coexistence and reconciliation is strongly recommended, and Copilot readiness should be part of the plan.',
    };
  }
  if (percent >= 30) {
    return {
      level: 'Moderate',
      label: 'Moderate complexity',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'A manageable migration, but with enough moving parts — permissions, data volume, or downtime constraints — that a staged, managed cutover protects you from the common failure modes. Worth planning rather than improvising.',
    };
  }
  return {
    level: 'Low',
    label: 'Low complexity',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'A relatively straightforward migration. Even so, a managed move ensures calendars, contacts, rules, and permissions all carry over cleanly — and sets you up for a safe Copilot rollout if you ever want it.',
  };
}

export default function ReadinessClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const notes: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.weight;
      if (option.note) notes.push(option.note);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, notes, ...levelFor(percent) };
  }, [answers]);

  const copilotPriority = useMemo(() => {
    const a = answers.find((ans) => ans.questionId === 'copilot_interest');
    return a ? a.optionIndex <= 1 : false; // "soon" or "within a year"
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const noteLines = result.notes.length
      ? result.notes.map((n, i) => `${i + 1}. ${n}`).join('\n')
      : 'No major complications flagged — a clean, straightforward migration.';

    return `MICROSOFT 365 MIGRATION READINESS CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Migration complexity: ${result.label} (${result.percent}% complexity score)

${result.blurb}

ANSWERS:
${answerLines}

PLANNING CONSIDERATIONS:
${noteLines}`;
  }, [result, answers]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📨</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              How complex is your move to Microsoft 365?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on your source platform, mailbox count, data volume, permissions, Copilot plans, and
              downtime tolerance. Get an instant complexity result and what to plan for first.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your migration is Low, Moderate, or High complexity</li>
              <li>• The specific things that need planning before cutover</li>
              <li>• Whether you should fix permissions before turning on Copilot</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Readiness Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Migration Readiness Check</span>
              <span>
                Question {currentIndex + 1} of {QUESTIONS.length}
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Moderate' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>{result.label}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Complexity score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Planning considerations */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.notes.length ? 'What to plan for first' : 'No Major Complications Flagged'}
          </h3>
          {result.notes.length ? (
            <ul className="space-y-3">
              {result.notes.map((note, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">
                    {i + 1}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{note}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">
              Your migration looks straightforward. A managed move still keeps calendars, contacts, rules, and
              permissions intact end to end.
            </p>
          )}
        </div>

        {/* Copilot callout (conditional) */}
        {copilotPriority && (
          <div className="rounded-2xl border-2 border-[#2cc3d1]/40 bg-[#2cc3d1]/10 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              You&apos;re planning Copilot &mdash; read this first
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Copilot surfaces whatever a user can already access. Migrating is the ideal moment to clean up permission
              sprawl so Copilot doesn&apos;t expose data the day it goes live.
            </p>
            <Link
              href="/services/microsoft-365-migration/copilot-readiness"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white dark:bg-gray-800 border border-[#2cc3d1] px-6 py-2.5 font-semibold text-[#2813e8] dark:text-[#7c9cff] transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              See the Copilot Readiness Assessment
            </Link>
          </div>
        )}

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want a zero-downtime cutover plan?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ runs managed Microsoft 365 migrations with BitTitan and AvePoint &mdash; no lost email, no
            broken permissions, no downtime.
          </p>
          <Link
            href="/services/microsoft-365-migration"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See the Managed Migration Service
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture — inline, POSTs to /api/contact tagged src:'m365-migration' */}
        <div className="mt-8">
          <LeadCaptureForm
            source="m365-migration"
            title="Get a Migration Plan from a Microsoft 365 Expert"
            description="Our team will review your readiness results and send a planned, zero-downtime migration approach for your move to Microsoft 365 — including Copilot readiness if you need it."
            deliverable="a zero-downtime Microsoft 365 migration plan"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
